import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { ClinicInfo, Patient, Physician, PrimaryPhysicianSuggestion } from 'generated/api';
import React, { MouseEvent, useEffect, useMemo, useState } from 'react';
import './style.scss';
import PhysicianUtils from 'services/PhysicianUtils';
import DefaultPopup from 'components/Utils/DefaultPopup';
import DefaultButton from 'components/Utils/NewDefaultButton';
import Icon from 'components/Utils/NewIcon';
import { api } from 'careand-redux/api/ApiThunks';
import { PhysicianImage } from 'components/Common/UserImage';
import { EditableClinicBadge } from 'scenes/HomeStaff/ClinicsDirectory';
import { store } from 'careand-redux/store';
import DefaultPatientModal from 'components/Utils/DefaultPatientModal';
import SelectClinic from 'scenes/HomePatient/AppointmentBooking/SelectClinic';
import { ActivePatient } from 'careand-redux/selectors';
import { useIsLoading } from 'careand-redux/slices/ui/hooks';
import ProgressMessage from 'components/Utils/ProgressMessage';

type Props = {
  pageContentOnly?: boolean;
  onSuccess?: (practitioner: Physician) => void;
  requiresConfirmation?: boolean;
  fullHeightPopup?: boolean;
  patient: Patient;
  // Optional dismiss affordance. When provided (e.g. launched from the Carey
  // chat), a close button is rendered so the user can back out. Omitted in the
  // existing in-page flows (subscription/booking), so nothing changes there.
  onClose?: () => void;
};

/**
 * @param {any} props
 */
const ChoosePrimaryPage = ({
  pageContentOnly = false,
  onSuccess,
  requiresConfirmation = true,
  fullHeightPopup = false,
  patient,
  onClose,
}: Props) => {
  const dispatch = useAppDispatch();
  const loading = useIsLoading(api.Patients.updatePatient);
  const loadingSuggestions = useIsLoading(api.Physicians.getPrimaryPhysicianSuggestions);
  const [practitionerPopup, setPractitionerPopup] = useState(false);
  const [selectedSuggestion, selectSuggestion] = useState<PrimaryPhysicianSuggestion | undefined>();
  const [suggestions, setSuggestions] = useState<PrimaryPhysicianSuggestion[]>([]);
  const [clinicModal, setClinicModal] = useState(false);

  const initialClinic = useMemo(() => {
    const state = store.getState();
    const patientClinicId = ActivePatient(state)?.clinic_id;
    let clinic = state.resource.clinics[0];
    if (patientClinicId) {
      let patientClinic = state.resource.clinics.find((it) => it.id === patientClinicId);
      clinic = patientClinic || clinic;
    }
    return clinic;
  }, []);

  const initialPractitioner = useMemo(() => {
    const state = store.getState();
    const pid = ActivePatient(state)?.primary_physician;
    if (pid) {
      return state.physicians.byId[pid];
    }
  }, []);

  const [clinic, setClinic] = useState<ClinicInfo | undefined>(initialClinic);
  const [selectedPractitioner, setSelectedPractitioner] = useState<Physician | undefined>(
    initialPractitioner,
  );

  const confirmButtonEnabled = !!selectedPractitioner;
  const confirmButtonTitle = confirmButtonEnabled
    ? `Select ${selectedPractitioner!.first_name}`
    : 'Select a Practitioner to Continue';

  useEffect(() => {
    if (!clinic) {
      return;
    }
    dispatch(api.Physicians.getPrimaryPhysicianSuggestions({ clinicId: clinic.id })).then((it) =>
      setSuggestions(it || []),
    );
  }, [clinic, dispatch]);

  function onSelectPractitionerFromPopup(practitioner: Physician) {
    setSelectedPractitioner(practitioner);
    if (!requiresConfirmation) {
      onConfirm(practitioner);
    }
  }

  function onSelectClinic(selectedClinic: ClinicInfo) {
    setClinic(selectedClinic);
    setClinicModal(false);
  }

  function onConfirm(practitioner?: Physician) {
    practitioner = practitioner || selectedPractitioner;
    if (!patient || !practitioner) {
      return;
    }

    const clinic_id = practitioner.clinic_id;
    const primary_physician = practitioner.id;

    const newPatient: Patient = {
      ...patient!,
      clinic_id,
      primary_physician,
      has_selected_primary: true,
    };
    dispatch(api.Patients.updatePatient({ id: newPatient.id!, patient: newPatient })).then(
      () => onSuccess?.(practitioner!),
    );
  }

  const popupHandler = useMemo(() => {
    return [!!practitionerPopup, setPractitionerPopup] as const;
  }, [practitionerPopup]);

  const content = (
    <div className="page-content choose-primary-page-content" key={clinic?.id}>
      {onClose && (
        <div className="choose-primary-close" onClick={onClose} role="button" aria-label="Close">
          <Icon src="xmark" width="20px" height="20px" />
        </div>
      )}
      <div className="prompt">
        Customize your care by choosing your clinic and primary practitioner
      </div>
      <EditableClinicBadge clinic={clinic} onClick={() => setClinicModal(true)} />
      <span className="text-s">
        {suggestions?.length > 0 ? suggestions?.length + ' ' : ''}Practitioners at {clinic?.name}
      </span>
      <div className="practitioner-list">
        {loadingSuggestions ? (
          <ProgressMessage />
        ) : (
          suggestions.map((it) => (
            <PractitionerSuggestionCard
              suggestion={it}
              key={it.physician.id}
              detail="availability"
              onClick={() => {
                selectSuggestion(it);
                setPractitionerPopup(true);
              }}
              selectedPractitioner={selectedPractitioner}
              clickable
            />
          ))
        )}
      </div>
      {requiresConfirmation && (
        <DefaultButton
          name={confirmButtonTitle}
          className="confirm-button"
          loading={loading}
          onClick={(e: MouseEvent<HTMLButtonElement>) => onConfirm()}
          disabled={!confirmButtonEnabled}
        />
      )}
      <DefaultPopup
        title="Select Primary Practitioner"
        className="select-primary-popup"
        openHandler={popupHandler}
      >
        <PractitionerPopupContent
          suggestion={selectedSuggestion}
          handler={popupHandler}
          onSelectPractitioner={onSelectPractitionerFromPopup}
        />
      </DefaultPopup>
      <DefaultPatientModal
        className="select-clinic-modal"
        title="Select Clinic"
        wrap={true}
        padding={false}
        fullHeight={fullHeightPopup}
        content={
          <SelectClinic
            initialClinic={clinic}
            patient={patient}
            onSuccess={onSelectClinic}
            submitBookingData={false}
            showSelectButton
          />
        }
        isOpen={!!clinicModal}
        onRequestClose={() => setClinicModal(false)}
      />
    </div>
  );

  return pageContentOnly ? content : <div className="page choose-primary-page">{content}</div>;
};

export default ChoosePrimaryPage;

export const PractitionerSuggestionCard = ({
  detail,
  showActionIcon,
  onClick,
  suggestion,
  selectedPractitioner,
  clickable,
}: {
  suggestion: PrimaryPhysicianSuggestion | undefined;
  detail?: 'availability' | 'specialty';
  showActionIcon?: boolean;
  onClick?: () => void;
  selectedPractitioner?: Physician;
  clickable?: boolean;
}) => {
  showActionIcon = showActionIcon ?? true;

  const practitioner = suggestion?.physician;
  const availability = suggestion?.availability;
  const selected = selectedPractitioner?.id === suggestion?.physician.id;

  let className = 'select-practitioner-card';
  if (selected) {
    className += ' selected';
  }
  if (clickable) {
    className += ' clickable';
  }

  return (
    <div className={className} onClick={onClick}>
      <PhysicianImage user={practitioner} />
      <div className="content">
        <span className="name">{PhysicianUtils.getName(practitioner)}</span>
        {detail === 'availability' && <AvailabilityBadge availability={availability} />}
        {detail === 'specialty' && (
          <span className="specialty">{practitioner?.specialty || 'NP'}</span>
        )}
      </div>
      {showActionIcon && <Icon className="action-icon" src="circle-info" />}
    </div>
  );
};

const AvailabilityBadge = ({ availability }: { availability?: 'high' | 'medium' | 'low' }) => {
  if (!availability) {
    return null;
  }
  return (
    <div className="availability-badge" data-availability={availability}>
      <div className="background" />
    </div>
  );
};

export const PractitionerPopupContent = ({
  suggestion,
  handler,
  onSelectPractitioner,
}: {
  suggestion: PrimaryPhysicianSuggestion | undefined;
  handler: Readonly<[boolean, (arg: boolean) => void]>;
  onSelectPractitioner?: (arg: Physician) => void;
}) => {
  const languages = (suggestion?.physician.languages ?? []).join(', ');

  function onClickConfirm() {
    if (onSelectPractitioner) {
      onSelectPractitioner(suggestion!.physician);
      close();
    }
  }

  function close() {
    handler[1](false);
  }

  const usualSchedule = useMemo(() => {
    if (suggestion?.usual_schedule) {
      return suggestion?.usual_schedule?.replace('-', ' - ');
    }
  }, [suggestion?.usual_schedule]);

  return (
    <div className="practitioner-popup-content">
      <PractitionerSuggestionCard
        suggestion={suggestion}
        detail="specialty"
        showActionIcon={false}
      />
      <AvailabilityBadge availability={suggestion?.availability} />
      <div className="sections">
        {suggestion?.usual_schedule && (
          <div className="section">
            <div className="title">Usual Schedule</div>
            <div className="content">{usualSchedule}</div>
          </div>
        )}
        {languages.length > 0 && (
          <div className="section">
            <div className="title">Languages</div>
            <div className="content">{languages}</div>
          </div>
        )}
      </div>
      {onSelectPractitioner && (
        <>
          <DefaultButton
            className="confirm-button"
            name={`Select ${suggestion?.physician?.first_name}`}
            onClick={onClickConfirm}
          />
          <DefaultButton className="cancel-button light" name={'Cancel'} onClick={close} />
        </>
      )}
    </div>
  );
};
